import { useState } from "react";
import { cn } from "@/lib/utils";

function TwitterIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function VerifiedBadge() {
  return (
    <svg className="size-4 text-[#C8963E]" viewBox="0 0 22 22" fill="currentColor">
      <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
    </svg>
  );
}

function TestimonialCard({
  className,
  avatar,
  username = "Sara K.",
  handle = "@saratattoos",
  content = "",
  date = "",
  verified = true,
  likes = 0,
  retweets = 0,
  tweetUrl,
  onHover,
  onLeave,
  isActive,
  onTap,
}) {
  const handleClick = (e) => {
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      if (!isActive) {
        e.preventDefault();
        onTap?.();
      }
    }
  };

  return (
    <a
      href={tweetUrl || "#"}
      target={tweetUrl ? "_blank" : undefined}
      rel="noopener noreferrer"
      onClick={handleClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      data-testid={`testimonial-card-${handle}`}
      className={cn(
        "relative flex h-auto min-h-[160px] sm:min-h-[200px] w-[280px] sm:w-[400px] -skew-y-[8deg] select-none flex-col rounded-2xl px-4 sm:px-5 py-4 sm:py-5 transition-all duration-500 no-underline",
        "border border-[rgba(200,150,62,0.25)] bg-[rgba(15,13,11,0.92)] backdrop-blur-sm",
        "hover:border-[rgba(200,150,62,0.6)] hover:bg-[#0F0D0B] cursor-pointer",
        isActive && "ring-2 ring-[#C8963E]/60",
        className
      )}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="size-10 sm:size-12 rounded-full bg-gradient-to-br from-[#C8963E] via-[#E8B86A] to-[#8a6628] flex items-center justify-center overflow-hidden shrink-0 border border-[rgba(200,150,62,0.4)]">
          {avatar ? (
            <img src={avatar} alt={username} className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg sm:text-2xl">✦</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="font-semibold text-[#F2EDE6] truncate text-sm sm:text-base">{username}</span>
            {verified && <VerifiedBadge />}
          </div>
          <span className="text-[rgba(242,237,230,0.45)] text-xs sm:text-sm">{handle}</span>
        </div>
        <TwitterIcon className="size-4 sm:size-5 text-[#F2EDE6] shrink-0" />
      </div>

      <p className="text-[#F2EDE6]/90 text-sm sm:text-[15px] leading-relaxed mb-3 line-clamp-4">
        {content}
      </p>

      <div className="flex items-center justify-between text-[rgba(242,237,230,0.4)] text-[11px] sm:text-sm mt-auto">
        <span>{date}</span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>{likes}</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
            <span>{retweets}</span>
          </div>
        </div>
      </div>
    </a>
  );
}

export default function Testimonials({ cards }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const getCardClassName = (index, baseClassName) => {
    const focusedIndex = hoveredIndex ?? activeIndex;
    if (focusedIndex === 0 && index === 1) {
      return baseClassName + " !translate-y-20 sm:!translate-y-32 !translate-x-14 sm:!translate-x-24";
    }
    if (focusedIndex === 0 && index === 2) {
      return baseClassName + " !translate-y-28 sm:!translate-y-44 !translate-x-24 sm:!translate-x-40";
    }
    if (focusedIndex === 1 && index === 2) {
      return baseClassName + " !translate-y-24 sm:!translate-y-40 !translate-x-24 sm:!translate-x-40";
    }
    return baseClassName;
  };

  const handleTap = (index) => {
    if (activeIndex === index) return;
    setActiveIndex(index);
  };

  const defaultCards = [
    {
      className:
        "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-[100%] before:outline before:outline-1 before:rounded-2xl before:outline-[rgba(200,150,62,0.25)] before:h-[100%] before:content-[''] before:bg-[rgba(8,7,6,0.6)] grayscale-[80%] hover:before:opacity-0 before:transition-opacity before:duration-500 hover:grayscale-0 before:left-0 before:top-0",
      avatar: "/img/testimonial-sara.jpg?v=2",
      username: "Sara K.",
      handle: "@sara.fineline",
      content:
        "Honestly didn't expect this much in just 3 weeks. Posted with the strategy and got 3 consult requests from IG — and one is already booked in for a full sleeve. Wild.",
      date: "Nov 14, 2025",
      verified: true,
      likes: 142,
      retweets: 23,
      tweetUrl: "https://www.inkflowleads.com",
    },
    {
      className:
        "[grid-area:stack] translate-x-8 sm:translate-x-16 translate-y-6 sm:translate-y-10 hover:-translate-y-1 before:absolute before:w-[100%] before:outline before:outline-1 before:rounded-2xl before:outline-[rgba(200,150,62,0.25)] before:h-[100%] before:content-[''] before:bg-[rgba(8,7,6,0.55)] grayscale-[80%] hover:before:opacity-0 before:transition-opacity before:duration-500 hover:grayscale-0 before:left-0 before:top-0",
      avatar: "/img/testimonial-marco.jpg?v=2",
      username: "Marco D.",
      handle: "@marco.blackwork",
      content:
        "I was tired of relying on walk-ins. The content plan alone changed everything — I actually know what to post and why. Calendar is fuller than it's been in 2 years.",
      date: "Oct 28, 2025",
      verified: true,
      likes: 89,
      retweets: 12,
      tweetUrl: "https://www.inkflowleads.com",
    },
    {
      className:
        "[grid-area:stack] translate-x-16 sm:translate-x-32 translate-y-12 sm:translate-y-20 hover:translate-y-6 sm:hover:translate-y-10",
      avatar: "/img/testimonial-elena.jpg?v=2",
      username: "Elena R.",
      handle: "@elena.tattoo",
      content:
        "Most coaches don't get the tattoo world. This one does. The weekly calls feel like talking to someone who actually understands what we deal with. Worth every euro.",
      date: "Oct 12, 2025",
      verified: true,
      likes: 218,
      retweets: 41,
      tweetUrl: "https://www.inkflowleads.com",
    },
  ];

  const displayCards = cards || defaultCards;

  return (
    <div className="grid [grid-template-areas:'stack'] place-items-center opacity-100">
      {displayCards.map((cardProps, index) => (
        <TestimonialCard
          key={index}
          {...cardProps}
          className={getCardClassName(index, cardProps.className || "")}
          onHover={() => setHoveredIndex(index)}
          onLeave={() => setHoveredIndex(null)}
          isActive={activeIndex === index}
          onTap={() => handleTap(index)}
        />
      ))}
    </div>
  );
}

export { TestimonialCard, Testimonials };
